import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { ExpenseReviewItem, ExpenseReviewService } from '../services/expense-review.service';

type ExpenseReviewRow = Omit<ExpenseReviewItem, 'bookingTotal' | 'amount'> & {
  bookingTotal: number;
  amount: number;
  transferDate: Date | null;
  transferTime: Date | null;
  hasWithholdingTax: boolean;
  withholdingTaxAmount: number;
};

@Component({
  selector: 'app-expense-review-page',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    StatusBadge,
    RefreshButtonComponent,
    TableModule,
    InputTextModule,
    DatePickerModule,
    CheckboxModule,
    InputNumberModule,
  ],
  templateUrl: './expense-review-page.component.html',
})
export class ExpenseReviewPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly expenseReviewService = inject(ExpenseReviewService);
  readonly expenses = signal<ExpenseReviewRow[]>([]);
  readonly isLoadingExpenses = signal(false);
  readonly selectedExpense = signal<ExpenseReviewRow | null>(null);
  readonly receiptUrlError = signal('');
  readonly transferDateTimeError = signal('');
  readonly withholdingTaxError = signal('');

  ngOnInit(): void {
    void this.loadExpenses();
  }

  async loadExpenses(): Promise<void> {
    if (this.isLoadingExpenses()) return;
    this.isLoadingExpenses.set(true);
    this.overlay.showLoading('กำลังโหลดใบขอเบิกรอตรวจสอบ...');
    try {
      const expenses = await this.expenseReviewService.getPending();
      this.expenses.set(
        expenses.map((expense) => ({
          ...expense,
          bookingTotal: Number(expense.bookingTotal),
          amount: Number(expense.amount),
          transferDate: null,
          transferTime: null,
          hasWithholdingTax: false,
          withholdingTaxAmount: 0,
        })),
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดใบขอเบิกไม่สำเร็จ', message);
    } finally {
      this.isLoadingExpenses.set(false);
      this.overlay.hideLoading();
    }
  }

  openExpenseDetail(row: ExpenseReviewRow): void {
    this.receiptUrlError.set('');
    this.transferDateTimeError.set('');
    this.withholdingTaxError.set('');
    if (row.status === 'WAITING_PAYMENT' && (!row.transferDate || !row.transferTime)) {
      const now = new Date();
      row.transferDate = new Date(now);
      row.transferTime = new Date(now);
    }
    this.selectedExpense.set(row);
  }
  closeExpenseDetail(): void {
    this.selectedExpense.set(null);
  }
  openAttachment(fileUrl: string | null): void {
    if (!fileUrl) return;
    window.open(this.expenseReviewService.resolveFileUrl(fileUrl), '_blank', 'noopener,noreferrer');
  }

  async approve(row: ExpenseReviewRow): Promise<void> {
    const receiptUrlError =
      row.status === 'WAITING_PAYMENT' ? this.validateReceiptUrl(row.receiptUrl) : '';
    this.receiptUrlError.set(receiptUrlError);
    const transferDateTimeError =
      row.status === 'WAITING_PAYMENT' && (!row.transferDate || !row.transferTime)
        ? 'กรุณาระบุวันและเวลาโอนเงิน'
        : '';
    this.transferDateTimeError.set(transferDateTimeError);
    const withholdingTaxError = this.validateWithholdingTax(row);
    this.withholdingTaxError.set(withholdingTaxError);
    if (receiptUrlError || transferDateTimeError || withholdingTaxError) return;
    const confirmed = await this.overlay.confirm({
      title: 'อนุมัติใบขอเบิก',
      message: `ยืนยันอนุมัติ ${row.code} จำนวน ${row.amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท หรือไม่`,
      confirmText: 'อนุมัติ',
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังอนุมัติใบขอเบิก...');
    try {
      await this.expenseReviewService.approve(
        row.id,
        row.status,
        row.receiptPath,
        row.status === 'WAITING_PAYMENT' ? row.receiptUrl.trim() : undefined,
        row.status === 'WAITING_PAYMENT' && row.transferDate
          ? this.toDateValue(row.transferDate)
          : undefined,
        row.status === 'WAITING_PAYMENT' && row.transferTime
          ? this.toTimeValue(row.transferTime)
          : undefined,
        row.status === 'WAITING_PAYMENT' && row.hasWithholdingTax
          ? row.withholdingTaxAmount
          : 0,
      );
      this.expenses.update((rows) => rows.filter((item) => item.id !== row.id));
      this.closeExpenseDetail();
      this.overlay.success('อนุมัติสำเร็จ', `${row.code} ได้รับการอนุมัติจากบัญชีแล้ว`);
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      if (error instanceof HttpErrorResponse && error.status === 409) {
        this.closeExpenseDetail();
        await this.loadExpenses();
      }
      this.overlay.error('อนุมัติไม่สำเร็จ', message);
    }
  }

  clearReceiptUrlError(): void {
    this.receiptUrlError.set('');
  }

  clearTransferDateTimeError(): void {
    this.transferDateTimeError.set('');
  }

  onWithholdingTaxChange(row: ExpenseReviewRow, checked: boolean): void {
    row.hasWithholdingTax = checked;
    if (!checked) row.withholdingTaxAmount = 0;
    this.withholdingTaxError.set('');
  }

  clearWithholdingTaxError(): void {
    this.withholdingTaxError.set('');
  }

  private validateWithholdingTax(row: ExpenseReviewRow): string {
    if (row.status !== 'WAITING_PAYMENT' || !row.hasWithholdingTax) return '';
    const amount = Number(row.withholdingTaxAmount);
    if (!Number.isFinite(amount) || amount <= 0) return 'กรุณากรอกยอดหัก ณ ที่จ่ายมากกว่า 0 บาท';
    if (amount >= row.amount) return 'ยอดหัก ณ ที่จ่ายต้องน้อยกว่ายอดที่ต้องโอน';
    return '';
  }

  private toDateValue(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toTimeValue(value: Date): string {
    return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
  }

  private validateReceiptUrl(value: string): string {
    if (!value.trim()) return 'กรุณากรอกลิงก์สลิปก่อนอนุมัติ';
    try {
      const url = new URL(value.trim());
      return url.protocol === 'http:' || url.protocol === 'https:'
        ? ''
        : 'ลิงก์สลิปต้องขึ้นต้นด้วย http:// หรือ https://';
    } catch {
      return 'รูปแบบลิงก์สลิปไม่ถูกต้อง';
    }
  }

  async reject(row: ExpenseReviewRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ไม่อนุมัติใบขอเบิก',
      message: `ยืนยันไม่อนุมัติ ${row.code} หรือไม่`,
      confirmText: 'ไม่อนุมัติ',
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังบันทึกผลการตรวจสอบ...');
    try {
      await this.expenseReviewService.reject(row.id, row.status, row.receiptPath);
      this.expenses.update((rows) => rows.filter((item) => item.id !== row.id));
      this.closeExpenseDetail();
      this.overlay.success('บันทึกผลแล้ว', `${row.code} ไม่ผ่านการตรวจสอบจากบัญชี`);
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      if (error instanceof HttpErrorResponse && error.status === 409) {
        this.closeExpenseDetail();
        await this.loadExpenses();
      }
      this.overlay.error('บันทึกผลไม่สำเร็จ', message);
    }
  }
}
